import {useRef, useEffect} from 'react';

interface UseVideoProgressBarProps {
    videoRef: React.RefObject<HTMLVideoElement>;
    setPlayedProgress: (progress: number) => void;
    setIsDragging: (isDragging: boolean) => void;
    isDragging: boolean;
    bufferedProgress: number;
}

export const useVideoProgressBar = ({
    videoRef,
    setPlayedProgress,
    setIsDragging,
    isDragging
}: UseVideoProgressBarProps) => {
    const progressBarRef = useRef<HTMLDivElement | null>(null);

    // Calculate position as a percentage
    const calculateProgress = (event: MouseEvent | TouchEvent): number => {
        const progressBar = progressBarRef.current;
        if (!progressBar) return 0;

        const rect = progressBar.getBoundingClientRect();
        let clientX: number;

        if (event instanceof MouseEvent) {
            clientX = event.clientX;
        } else {
            clientX = event.touches[0].clientX;
        }

        const relativeX = Math.min(
            Math.max(clientX - rect.left, 0),
            rect.width
        );
        return (relativeX / rect.width) * 100; // Return progress as a percentage
    };

    // Update video time based on progress
    const updateVideoTime = (progress: number) => {
        if (videoRef.current) {
            const newTime = (progress / 100) * videoRef.current.duration;
            videoRef.current.currentTime = newTime;
        }
    };

    // Handle drag start
    const handleDragStart = (event: React.MouseEvent | React.TouchEvent) => {
        setIsDragging(true);
        const newProgress = calculateProgress(event.nativeEvent);
        setPlayedProgress(newProgress);
        updateVideoTime(newProgress);
    };

    // Handle drag move
    const handleDragMove = (event: MouseEvent | TouchEvent) => {
        if (!isDragging) return;

        const newProgress = calculateProgress(event);
        setPlayedProgress(newProgress);
        updateVideoTime(newProgress);
    };

    // Handle drag end
    const handleDragEnd = () => {
        setIsDragging(false);
    };

    // Sync progress with video time
    useEffect(() => {
        const video = videoRef.current;

        const handleTimeUpdate = () => {
            if (video && !isDragging) {
                const currentProgress =
                    (video.currentTime / video.duration) * 100;
                setPlayedProgress(currentProgress);
            }
        };

        if (video) {
            video.addEventListener('timeupdate', handleTimeUpdate);
        }

        return () => {
            if (video) {
                video.removeEventListener('timeupdate', handleTimeUpdate);
            }
        };
    }, [isDragging, videoRef, setPlayedProgress]);

    // Add global event listeners for drag events
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('touchmove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchend', handleDragEnd);

            return () => {
                document.removeEventListener('mousemove', handleDragMove);
                document.removeEventListener('touchmove', handleDragMove);
                document.removeEventListener('mouseup', handleDragEnd);
                document.removeEventListener('touchend', handleDragEnd);
            };
        }
    }, [isDragging]);

    return {
        progressBarRef,
        handleDragStart
    };
};
